"use client";

/**
 * NewCreditCardForm Component with Solana Support
 *
 * This component allows users to purchase tokens using credit cards via Wert.
 * It now supports both EVM chains (Ethereum, BSC, Base) and Solana.
 *
 * SOLANA INTEGRATION:
 *
 * This component uses LiFi's route aggregator to handle Solana token swaps.
 * When a user wants to purchase a Solana token:
 *
 * 1. The component calculates the SOL amount needed based on USD input
 * 2. Uses LiFi to find the best route from SOL to the target SPL token
 * 3. Gets the swap instruction data from LiFi's route
 * 4. Passes this instruction data to Wert for execution
 *
 * The component automatically handles:
 * - Route finding and optimization
 * - Slippage tolerance
 * - Transaction fee estimation
 * - Error handling for failed routes
 * - Proper encoding of Solana instructions for Wert
 *
 * WERT INTEGRATION:
 * The component properly encodes Solana instructions according to Wert's requirements:
 * - Creates instruction object with program_id, accounts, and data
 * - Includes required accounts (user wallet, Wert funding account, system program, token program)
 * - Stringifies the instruction object and hex encodes it
 * - Passes the encoded instruction to Wert's sc_input_data parameter
 *
 * IMPORTANT: Test thoroughly on devnet before deploying to mainnet.
 */

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
  FormItem,
} from "@/components/ui/form";
import { ExternalLinkIcon, Loader2, CheckCircle2 } from "lucide-react";
import WertWidget from "@wert-io/widget-initializer";
import type { Options } from "@wert-io/widget-initializer/types";
import { formatEther, encodeFunctionData } from "viem";
import { toast } from "sonner";

// Payment status listener for Wert widget - shows notification when purchase completes
const createPaymentStatusListeners = (): Options["listeners"] => ({
  "payment-status": (data) => {
    // Wert statuses: 'pending', 'success', 'failed', 'cancelled'
    if (data.status === "success" || data.status === "pending") {
      toast.success(
        "Your tokens are on the way! Credit card purchases may take 5-10 minutes to appear in your wallet. Please be patient.",
        {
          duration: 10000, // 10 seconds
          icon: <CheckCircle2 className="h-5 w-5 text-green-500" />,
        }
      );
    }
  },
});
import { CardProcessor, processorAddresses } from "@/constants";
import { zeroAddress, parseUnits, Address } from "viem";

import {
  useTokenStore,
  useUserSettingsStore,
  useCreditCardBuyStore,
} from "@/lib/zustand";
import { usePortfolioStore } from "../wallet/mobile-portfolio-toggle";
import { getStepTransaction, Route, getRoutes, BalanceError } from "@lifi/sdk";
import { ChainId } from "@lifi/sdk";
import { PublicKey } from "@solana/web3.js";
import { useWallet as useSolanaWallet } from "@solana/wallet-adapter-react";
import { useWallet as useParaWallet } from "@getpara/react-sdk";
import {
  dataToHexString,
  getOrCreateAssociatedTokenAccountIX,
  WERT_HOT_WALLET,
} from "@/services";
import { useParaAccount } from "@/hooks/use-appkit-account";
import {
  createWertSolanaIx,
} from "@/lib/wert-solana-ix";
import { SOLANA_PROGRAM_ID } from "@/lib/solana";
import Image from "next/image";
import { SWAP_FEE } from "@/config";
import { WertRegionsModal } from "../modals/wert-regions-modal";

const schema = z.object({
  cardContributionAmount: z.string(),
});

// Add OptimalPath type
type OptimalPath = {
  maxReceiveAmount: string;
  optimalRouter: Address;
};

type PaymentMethod = "apple-pay" | "google-pay";
const SOL_MINT = "So11111111111111111111111111111111111111112";

const isValidSolanaAddress = (addr: string | null | undefined): boolean => {
  if (!addr) return false;
  if (addr.startsWith("0x")) return false;
  return addr.length >= 32 && addr.length <= 44;
};

const CardHelpTooltip = () => {
  const [showCardHelp, setShowCardHelp] = useState(false);
  const [showRegionsModal, setShowRegionsModal] = useState(false);

  return (
    <div className="flex flex-col items-center mt-2">
      <button
        type="button"
        onClick={() => setShowCardHelp(true)}
        className="text-xs text-gray-400 hover:text-gray-300 underline underline-offset-2 transition-colors"
      >
        Having issues buying with a card?
      </button>
      {showCardHelp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setShowCardHelp(false)}
          />
          <div className="relative z-10 bg-[#111] border border-white/10 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <button
              type="button"
              onClick={() => setShowCardHelp(false)}
              className="absolute top-4 right-4 p-1 rounded-lg hover:bg-white/10 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h3 className="text-white font-semibold text-base mb-3 pr-8">
              Trouble with card purchases?
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-3">
              Some banks automatically block crypto-related purchases as a security measure.
            </p>
            <p className="text-gray-400 text-sm leading-relaxed mb-3">
              <span className="text-white font-medium">What to do:</span> Call your bank or card provider and ask them to allow transactions from <span className="text-white">Wert</span> — the payment processor used by ForgeSwap.
            </p>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              Alternatively, try paying with <span className="text-white">Apple Pay</span> or <span className="text-white">Google Pay</span> which often have fewer restrictions.
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setShowCardHelp(false)}
                className="w-full py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition-colors"
              >
                Got it
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCardHelp(false);
                  setShowRegionsModal(true);
                }}
                className="w-full py-2 rounded-xl border border-white/10 hover:bg-white/5 text-gray-400 hover:text-white text-sm font-medium transition-colors"
              >
                Check supported regions
              </button>
            </div>
          </div>
        </div>
      )}
      <WertRegionsModal
        isOpen={showRegionsModal}
        onClose={() => setShowRegionsModal(false)}
      />
    </div>
  );
};

export const NewCreditCardForm = ({
  paymentMethod,
  autoOpen,
  defaultAmount,
}: {
  paymentMethod?: PaymentMethod;
  autoOpen?: boolean;
  defaultAmount?: string;
}) => {
  const { address, connection } = useParaAccount();
  const { data: paraWallet } = useParaWallet();
  const { publicKey: solanaPublicKey } = useSolanaWallet();
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      cardContributionAmount: defaultAmount || "5",
    },
  });

  // Update form when defaultAmount prop changes (e.g., LiFi quote completes after modal opened),
  // but only if the user hasn't manually edited the field. dirtyFields tracks user interactions.
  useEffect(() => {
    if (
      defaultAmount &&
      defaultAmount !== form.getValues("cardContributionAmount") &&
      !form.formState.dirtyFields.cardContributionAmount
    ) {
      form.setValue("cardContributionAmount", defaultAmount);
    }
  }, [defaultAmount, form]);

  const { toToken } = useTokenStore();
  const { userSettings } = useUserSettingsStore();
  const { isWertWidgetOpen, setIsWertWidgetOpen, setIsLoadingSwap } =
    useCreditCardBuyStore();

  const { setIsOpen } = usePortfolioStore();

  // Monitor for widget closure
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isWertWidgetOpen) {
        // Widget might have been closed when user switched tabs or closed browser
        setIsWertWidgetOpen(false);
      }
    };

    const handleBeforeUnload = () => {
      if (isWertWidgetOpen) {
        setIsWertWidgetOpen(false);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isWertWidgetOpen]);

  // Auto-open widget with a preselected payment method if requested
  useEffect(() => {
    if (!autoOpen || !paymentMethod) return;
    // Defer to next tick to ensure modal content mounts
    const t = setTimeout(() => {
      openWertWidget(paymentMethod);
    }, 0);
    return () => clearTimeout(t);
  }, [autoOpen, paymentMethod]);

  // Add optimal path state and default value
  const default_optimal_path: OptimalPath = {
    maxReceiveAmount: "0",
    optimalRouter: zeroAddress,
  };
  // const [optimalPath, setOptimalPath] =
  //   useState<OptimalPath>(default_optimal_path);
  // const [amountWillReceive, setAmountWillReceive] = useState<string>("0");

  // function to calculate BNB amount from USD
  const calculateBnbAmount = async (usdAmount: string): Promise<number> => {
    if (!usdAmount) return 0;
    try {
      const response = await fetch(
        "https://api.dexscreener.com/tokens/v1/bsc/0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c",
        { headers: { Accept: "application/json" } }
      );
      if (!response.ok) throw new Error("DexScreener fetch failed");
      const pairs: any[] = await response.json();
      const price = Number(pairs?.[0]?.priceUsd || 0);
      if (price > 0) return parseFloat(usdAmount) / price;
      throw new Error("No price found");
    } catch {
      return parseFloat(usdAmount) / 600; // fallback
    }
  };

  // function to calculate ETH amount from USD
  const calculateETHAmount = async (usdAmount: string): Promise<number> => {
    if (!usdAmount) return 0;
    try {
      const response = await fetch(
        "https://api.dexscreener.com/tokens/v1/ethereum/0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2",
        { headers: { Accept: "application/json" } }
      );
      if (!response.ok) throw new Error("DexScreener fetch failed");
      const pairs: any[] = await response.json();
      const price = Number(pairs?.[0]?.priceUsd || 0);
      if (price > 0) return parseFloat(usdAmount) / price;
      throw new Error("No price found");
    } catch {
      return parseFloat(usdAmount) / 3500; // fallback
    }
  };

  // function to calculate SOL amount from USD
  const calculateSOLAmount = async (usdAmount: string): Promise<number> => {
    if (!usdAmount) return 0;
    try {
      const response = await fetch(
        "https://api.dexscreener.com/tokens/v1/solana/So11111111111111111111111111111111111111112",
        { headers: { Accept: "application/json" } }
      );
      if (!response.ok) throw new Error("DexScreener fetch failed");
      const pairs: any[] = await response.json();
      const price = Number(pairs?.[0]?.priceUsd || 0);
      if (price > 0) return parseFloat(usdAmount) / price;
      throw new Error("No price found");
    } catch {
      return parseFloat(usdAmount) / 130; // fallback
    }
  };

  const APPROVED_CHAIN_IDS = [56, 8453, 1, ChainId.SOL];
  const CARD_PROCESSOR_ADDRESSES: Record<number, Address> = processorAddresses as Record<number, Address>;

  const networkAssetInfo: Record<
    number,
    {
      network: string;
      commodity: string;
      priceFunction: (usdAmount: string) => number | Promise<number>;
    }
  > = {
    56: {
      network: "bsc",
      commodity: "BNB",
      priceFunction: calculateBnbAmount,
    },
    8453: {
      network: "base",
      commodity: "ETH",
      priceFunction: calculateETHAmount,
    },
    1: {
      network: "ethereum",
      commodity: "ETH",
      priceFunction: calculateETHAmount,
    },
    [ChainId.SOL]: {
      network: "solana",
      commodity: "SOL",
      priceFunction: calculateSOLAmount,
    },
  };

  // Helper function to encode CardProcessor buyToken call
  const encodeCardProcessorCall = (
    token: Address,
    router: Address,
    minOut: bigint,
    to: Address
  ): string => {
    return encodeFunctionData({
      abi: CardProcessor.abi,
      functionName: "buyToken",
      args: [token, router, minOut, to],
    });
  };

  // Update getSignature function to handle Solana
  const getSignature = async (
    commodity: string,
    network: string,
    contract_address: Address | string,
    buyAmount: number,
    data: Address | string,
    recipientAddress?: string
  ) => {
    const signatureAddress =
      recipientAddress || address || "0x3f1b0334ae9c405D94f330c9460D98079349f2aC";

    const url = `/api/secure-route?address=${
      signatureAddress
    }&buyAmount=${buyAmount}&network=${network}&commodity=${commodity}&contractAddress=${contract_address}&inputData=${data}`;

    const resp = await fetch(url);
    if (resp.ok) {
      const data = await resp.json();
      console.log("resp: ", data);
      return data;
    }
    const errorText = await resp.text();
    console.error("Wert signature request failed", {
      status: resp.status,
      statusText: resp.statusText,
      url,
      errorText,
      network,
      commodity,
      contract_address,
      recipientAddress,
    });
    return null;
  };

  // const partnerID = process.env.NEXT_PUBLIC_WERT_PARTNER_ID_SANDBOX; // SANDBOX ID
  const partnerID = process.env.NEXT_PUBLIC_WERT_PARTNER_ID_PRODUCTION;
  const origin = process.env.NEXT_PUBLIC_WERT_ORIGIN;

  const otherWidgetOptions: Options = {
    partner_id: partnerID || "", // your partner id
    // click_id: uuidv4(), // unique id of purhase in your system
    origin: origin || "https://widget.wert.io", // prodiction origin
    // extra: nftOptions,
    theme: "dark",
  };

  const openGeneralizedWidget = async (
    paymentMethod?: "apple-pay" | "google-pay",
    options?: {
      commodity?: string;
      network?: string;
      targetMint?: string;
    },
    buyAmountParam?: number
  ) => {
    if (isWertWidgetOpen) {
      // toast.error("A payment widget is already open. Please close it first.");
      return;
    }

    const buyAmountForRoute = buyAmountParam ?? 0.01;
    const solanaAddress =
      solanaPublicKey?.toBase58() ||
      (((paraWallet as any)?.email || (paraWallet as any)?.userId || (paraWallet as any)?.externalId) ? null : (paraWallet as any)?.address) ||
      (isValidSolanaAddress(address) ? address : null) ||
      null;
    if (!solanaAddress) {
      toast.error("Please connect a Solana wallet");
      return;
    }

    const widgetCommodity = options?.commodity || "SOL";
    const widgetNetwork = options?.network || "solana";
    const targetMint = options?.targetMint || "So11111111111111111111111111111111111111112";

    console.log("new-credit-card-form: opening generalized Solana widget", {
      commodity: widgetCommodity,
      network: widgetNetwork,
      targetMint,
      solanaAddress,
    });

    const allInstructions = createWertSolanaIx(
      solanaAddress,
      buyAmountForRoute,
      new PublicKey(targetMint),
      100
    );

    try {
      setIsWertWidgetOpen(true);
      // Close portfolio sidebar if open
      setIsOpen(false);
      
      const wertScInputData = dataToHexString(allInstructions);

      const signature = await getSignature(
        widgetCommodity,
        widgetNetwork,
        SOLANA_PROGRAM_ID,
        buyAmountForRoute,
        wertScInputData
      );

      if (!signature) {
        console.error("Error getting signature for generalized Solana Wert widget", {
          network: "solana",
          commodity: widgetCommodity,
          solanaAddress,
          buyAmountForRoute,
          scInputData: wertScInputData,
        });
        toast.error("Error Getting Signature");
        return;
      }

      const wertWidget = new WertWidget({
        ...signature,
        ...otherWidgetOptions,
        ...(paymentMethod ? { payment_method: paymentMethod } : {}),
        listeners: createPaymentStatusListeners(),
      });
      wertWidget.open();
      setIsWertWidgetOpen(false);
    } catch (error) {
      console.error("Error:", error);
      setIsWertWidgetOpen(false);
    } finally {
      setIsWertWidgetOpen(false);
    }
  };

  // Update openWertWidget to include Solana support
  const openWertWidget = async (
    paymentMethod?: "apple-pay" | "google-pay"
  ) => {
    if (!connection) {
      // toast.error("A payment widget is already open. Please close it first.");
      return;
    }
    if (isWertWidgetOpen) {
      // toast.error("A payment widget is already open. Please close it first.");
      return;
    }

    setIsWertWidgetOpen(true);
    // Close portfolio sidebar if open
    setIsOpen(false);

    // Always use the form amount - user can edit it before submitting any payment method
    const usdAmount = form.getValues("cardContributionAmount");

    if (!usdAmount) {
      toast.error("Please enter a valid USD Amount");
      return;
    }

    if (Number(usdAmount) < 5) {
      toast.error("Minimum Card Purchase Of $5 USD");
      return;
    }

    if (!APPROVED_CHAIN_IDS.includes(toToken.chainId)) {
      toast.error("Chain Not Supported For Card Purchase");
      return;
    }

    if (!Object.keys(networkAssetInfo).includes(toToken.chainId.toString())) {
      toast.error("Chain Not Supported For Card Purchase");
      return;
    }

    const networkInfo =
      networkAssetInfo[toToken.chainId as keyof typeof networkAssetInfo];

    // Handle async SOL price calculation
    let buyAmount: number;
    const priceResult = networkInfo.priceFunction(usdAmount.toString());
    if (priceResult instanceof Promise) {
      buyAmount = await priceResult;
    } else {
      buyAmount = priceResult;
    }

    const network = networkInfo.network;
    const commodity = networkInfo.commodity;

    // Handle Solana differently
    if (toToken.chainId === ChainId.SOL) {
      const solanaAddress =
        solanaPublicKey?.toBase58() ||
        (((paraWallet as any)?.email || (paraWallet as any)?.userId || (paraWallet as any)?.externalId) ? null : (paraWallet as any)?.address) ||
        (isValidSolanaAddress(address) ? address : null) ||
        null;
      if (!solanaAddress) {
        toast.error("Please connect a Solana wallet");
        setIsWertWidgetOpen(false);
        return;
      }

      // Use 4 decimals for SOL to reduce USD display gap while maintaining sufficient precision
      const buyAmountForRoute = parseFloat(buyAmount.toFixed(4));
      let allInstructions: any;

      if (
        toToken.address === "11111111111111111111111111111111" ||
        toToken.symbol === "SOL"
      ) {
        allInstructions = createWertSolanaIx(
          solanaAddress,
          buyAmountForRoute,
          new PublicKey("So11111111111111111111111111111111111111112"),
          100
        );
      } else {
        allInstructions = createWertSolanaIx(
          solanaAddress,
          buyAmountForRoute,
          new PublicKey(toToken.address),
          100
        );
      }

      try {
        const wertScInputData = dataToHexString(allInstructions);
        const signature = await getSignature(
          commodity,
          network,
          SOLANA_PROGRAM_ID,
          buyAmountForRoute,
          wertScInputData,
          solanaAddress
        );

        if (!signature) {
          toast.error("Error Getting Signature");
          return;
        }

        const wertWidget = new WertWidget({
          ...signature,
          ...otherWidgetOptions,
          ...(paymentMethod ? { payment_method: paymentMethod } : {}),
          listeners: createPaymentStatusListeners(),
        });

        wertWidget.open();
      } catch (error) {
        console.error("Wert widget open error:", error);
        toast.error("Failed to open payment widget");
        setIsWertWidgetOpen(false);
      } finally {
        setIsWertWidgetOpen(false);
      }
    } else {
      try {
        // Handle EVM chains
        const buyAmountForRoute =
          Math.floor(buyAmount * 100_000_000) / 100_000_000;
        // Check if CardProcessor is available for this chain
        const cardProcessorAddress = CARD_PROCESSOR_ADDRESSES[toToken.chainId];
        let contractAddress: Address;
        let inputData: string;
        
        if (cardProcessorAddress) {
          const routerAddress = zeroAddress;
          const minOut = BigInt(0);
          
          // Encode CardProcessor buyToken call
          // Parameters: (token, router, minOut, to)
          const tokenAddress = toToken.address === zeroAddress 
            ? zeroAddress 
            : (toToken.address as Address);
          
          inputData = encodeCardProcessorCall(
            tokenAddress,
            routerAddress,
            minOut,
            (address || "0x3f1b0334ae9c405D94f330c9460D98079349f2aC") as Address
          );
          
          contractAddress = cardProcessorAddress;
        } else {
          // Fallback to direct router call if CardProcessor not available for this chain
          contractAddress = zeroAddress;
          inputData = "0x";
        }
        
        const signature = await getSignature(
          commodity,
          network,
          contractAddress,
          buyAmountForRoute,
          inputData
        );
        if (!signature) {
          toast.error("Error Getting Signature");
          return;
        }

        setIsWertWidgetOpen(true);
        console.log("USD amount for Apple/Google Pay:", usdAmount);
        const wertWidget = new WertWidget({
          ...signature,
          ...otherWidgetOptions,
          ...(paymentMethod ? { payment_method: paymentMethod } : {}),
          listeners: createPaymentStatusListeners(),
        });
        wertWidget.open();
        setIsWertWidgetOpen(false);

      } catch (error) {
        console.error("Error opening Wert widget:", error);
        toast.error("Failed to open payment widget");
        setIsWertWidgetOpen(false);
      } finally {
        setIsWertWidgetOpen(false);
      }
    }
    setIsWertWidgetOpen(false);

  };

  const getSwapRoute = async (fromAmount: string) => {
    // Early validation checks
    if (!address) {
      toast.error("Please connect your wallet");
      return;
    }

    setIsLoadingSwap(true);
    console.log("Slippage: ", Number(userSettings.slippage) + 0.1);
    try {
      // Prepare route request
      const routeRequest = {
        fromChainId: toToken.chainId,
        toChainId: toToken.chainId,
        fromTokenAddress: zeroAddress,
        toTokenAddress: toToken?.address,
        fromAmount: parseUnits(fromAmount || "0", 18).toString(),
        fromAddress: address,
        toAddress: address,
        options: {
          fee: SWAP_FEE,
          slippage: Number(userSettings.slippage) + 0.1,
          // Disable LiFi's default 10% price impact ceiling. Stale price feeds for low-cap tokens
          // cause false high-impact rejections; the actual swap executes at Jupiter's real market rate.
          maxPriceImpact: 1,
        },
      };

      // Get and validate routes
      const routesResponse = await getRoutes(routeRequest);

      if (!routesResponse.routes?.length) {
        throw new Error(
          "No routes found for this swap. Try increasing slippage or trying another pair."
        );
      }

      // Find best route
      const recommendedRoute =
        routesResponse.routes.find((route) =>
          route.tags?.includes("RECOMMENDED")
        ) || routesResponse.routes[0];

      if (!recommendedRoute) {
        throw new Error("No valid routes available. Please try again.");
      }

      return await executeRouteSteps(recommendedRoute);
    } catch (error) {
      console.error("Swap error:", error);
      if (error instanceof BalanceError) {
        throw new Error("Insufficient funds for this swap.");
      } else if ((error as any)?.message?.includes("User denied")) {
        throw new Error("Transaction rejected by user");
      }
      throw new Error("An error occurred while swapping. Please try again.");
    } finally {
      setIsLoadingSwap(false);
    }
  };

  //   async function getDataForCardProcessor() {

  //     // get the route
  //     const route = await

  //   }

  // Simplified example function to execute each step of the route sequentially
  async function executeRouteSteps(route: Route) {
    const step = route.steps[0];

    // Request transaction data for the current step
    const thisStep = await getStepTransaction(step);

    if (!thisStep.transactionRequest) {
      throw new Error(
        "No route found for this swap. Try increasing slippage or trying another token."
      );
    }

    console.log("This Step: ", thisStep);

    // Handle Solana transactions differently
    if (route.fromChainId === ChainId.SOL) {
      // For Solana, we only have the data property
      const data = thisStep.transactionRequest.data;

      if (!data) {
        throw new Error("Invalid Solana transaction request data");
      }

      // For Solana, we need to extract the program ID from the route
      // The program ID is typically the Jupiter program for swaps
      const contract_address = SOLANA_PROGRAM_ID; // Jupiter program ID

      console.log({
        data: data,
        contract_address: contract_address,
        value: "0", // Solana doesn't use value in the same way as EVM
      });

      // Get the expected output amount from the route
      const toAmount = route.steps[0]?.estimate?.toAmount || "0";

      return {
        data: data,
        contract_address: contract_address,
        value: "0",
        toAmount: toAmount, // Add toAmount for consistency
      };
    } else {
      // Handle EVM chains as before
      const data = thisStep.transactionRequest.data;
      const contract_address = thisStep.transactionRequest.to;
      const value = thisStep.transactionRequest.value;

      if (!data || !contract_address || !value) {
        throw new Error("Invalid transaction request data");
      }

      console.log({
        data: data,
        contract_address: contract_address,
        value: formatEther(BigInt(value)),
      });

      // Get the expected output amount from the route
      const toAmount = route.steps[0]?.estimate?.toAmount || "0";

      return {
        data: data,
        contract_address: contract_address,
        value: formatEther(BigInt(value)),
        toAmount: toAmount, // Add toAmount for minOut calculation
      };
    }
  }

  // Add display components for fees and minimum output
  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit(() => {
            openWertWidget();
          })();
        }}
        className="space-y-4 flex flex-col w-full"
      >
        {/* Amount Input */}
        <FormField
          control={form.control}
          name="cardContributionAmount"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <div className="relative w-full">
                  <Input
                    {...field}
                    placeholder="Enter USD amount..."
                    className="pl-8  placeholder:text-foreground/50 bg-foreground/10 border border-border outline-none text-foreground text-[20px] sm:text-[30px] font-medium h-[72px] rounded-2xl"
                  />
                  <p className="text-foreground/50 text-[20px] absolute top-1/2 transform -translate-y-1/2 left-4 -translate-x-1/2">
                    $
                  </p>
                </div>
              </FormControl>
              <FormDescription className="text-xs flex flex-col items-start gap-1">
                Minimum Card Purchase Of $5 USD. Must live in an area that is
                supported by Wert.
                <a
                  href="https://widget.wert.io/service-availability"
                  target="_blank"
                  className="underline text-blue-500 hover:text-blue-600 flex items-center gap-1"
                >
                  View Supported Locations{" "}
                  <ExternalLinkIcon className="w-2 h-2" />
                </a>
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* <div className="w-full flex justify-end">
          <CardSelectTokenModal />
        </div> */}

        {/* Estimated Slippage */}
        {/* <div className="mt-2">
          <div className="flex items-center gap-2">
            <p className="text-[14px] text-foreground">Estimated Slippage</p>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <InfoIcon className="w-4 h-4 cursor-help text-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Slippage is due to DEX fees, protocol fees, and token
                    buy/sell fees
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-[20px] text-foreground font-medium">~{1}%</p>
        </div> */}

        {/* Minimum Output Amount */}
        {/* <div className="mt-2">
          <div className="flex items-center gap-2">
            <p className="text-[14px] text-foreground">Minimum Output Amount</p>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <InfoIcon className="w-4 h-4 cursor-help text-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Minimum Amount Of Tokens To Receive From Swap</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div> */}
        {/* <p className="text-[20px] text-foreground font-medium"> */}
        {/* {getAmountToReceive().toLocaleString(undefined, {
              minimumFractionDigits: 4,
              maximumFractionDigits: 8,
            })}{" "}
            1,000 */}
        {/* {toToken.symbol} */}
        {/* </p> */}
        {/* </div> */}

        {/* Buy Button */}
        <Button type="submit" className="w-full mt-5">
          {isWertWidgetOpen ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <p>Buy {toToken?.symbol || ""} with card</p>
          )}
        </Button>

        {/* Express pay options - always use form amount */}
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1 rounded-[8px] border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)] px-3 py-2.5 text-xs text-[#aaa] hover:bg-[rgba(255,255,255,0.05)]"
            onClick={() => openWertWidget("apple-pay")}
          >
            <span className="flex items-center justify-center gap-2" aria-label="Apple Pay">
              <Image
                src="/applepay.png"
                alt="Apple Pay"
                width={54}
                height={18}
                className="h-[18px] w-auto"
              />
              <span className="whitespace-nowrap">Buy with Apple Pay</span>
            </span>
          </Button>
          <Button
            type="button"
            variant="outline"
            className="flex-1 rounded-[8px] border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)] px-3 py-2.5 text-xs text-[#aaa] hover:bg-[rgba(255,255,255,0.05)]"
            onClick={() => openWertWidget("google-pay")}
          >
            <span className="flex items-center justify-center gap-2">
              <Image
                src="/googlepay.png"
                alt="Google Pay"
                width={54}
                height={18}
                className="h-[18px] w-auto"
              />
              <span className="whitespace-nowrap">Buy with Google Pay</span>
            </span>
          </Button>
        </div>

        <CardHelpTooltip />

        {/* General Buy Button */}
        <p className="text-[14px] pl-2 text-primary">
          Trying To Buy{" "}
          {toToken.chainId == 56
            ? "BNB"
            : toToken.chainId == ChainId.SOL
              ? "SOL"
              : "ETH"}
          ? Click{" "}
          <button
            onClick={() => openGeneralizedWidget()}
            className="underline"
            type="button"
          >
            here
          </button>
        </p>
      </form>
    </Form>
  );
};
